// The school's own review form (/submit-review): the link the dashboard's
// "get reviews" page shares. Port of v1's SubmitReviewView: pick an event
// or a product, then name, email, stars and words. GET
// /widget/pages/review/:slug lists what can be reviewed, POST
// /widget/:slug/submit-review sends it. Pure.

import { EMAIL_REGEX } from "@baluu/ui/components/checkout/validation";

export type ReviewTargetType = "course" | "product";

export type SubmitReviewPage = {
  school: { _id?: string; slug?: string; name?: string } | null;
  courses: Array<{ _id: string; title: string }>;
  products: Array<{ _id: string; name: string }>;
};

export type SubmitReviewValues = {
  targetType: ReviewTargetType | null;
  targetId: string;
  firstName: string;
  lastName: string;
  showOnlyFirstName: boolean;
  email: string;
  /** 0–5 stars; 0 is none picked. */
  rating: number;
  text: string;
};

export type SubmitReviewErrors = Partial<Record<keyof SubmitReviewValues, string>>;

/** The kind to review when there's only one to choose (v1's default). */
export function initialTargetType(page: Pick<SubmitReviewPage, "courses" | "products">): ReviewTargetType | null {
  const courses = page.courses?.length ?? 0;
  const products = page.products?.length ?? 0;
  if (courses > 0 && products === 0) return "course";
  if (products > 0 && courses === 0) return "product";
  return null;
}

export const emptySubmitReview = (page: Pick<SubmitReviewPage, "courses" | "products">): SubmitReviewValues => ({
  targetType: initialTargetType(page),
  targetId: "",
  firstName: "",
  lastName: "",
  showOnlyFirstName: false,
  email: "",
  rating: 0,
  text: "",
});

/** The choices for the picked kind. */
export function choicesFor(page: Pick<SubmitReviewPage, "courses" | "products">, type: ReviewTargetType | null) {
  if (type === "course") return (page.courses ?? []).map((c) => ({ value: c._id, label: c.title }));
  if (type === "product") return (page.products ?? []).map((p) => ({ value: p._id, label: p.name }));
  return [];
}

/** Whether the school lists anything to review: with nothing, the API
 * would refuse every review, so the page says so instead of a form. */
export const takesReviews = (page: Pick<SubmitReviewPage, "courses" | "products">) =>
  (page.courses?.length ?? 0) + (page.products?.length ?? 0) > 0;

/** The form's checks: what the API requires (a class or product, first and
 * last name, a valid email, a rating), with v1's messages, plus the
 * booking review's length rule for the words. */
export function validateSubmitReview(v: SubmitReviewValues, page: Pick<SubmitReviewPage, "courses" | "products">): SubmitReviewErrors {
  const errors: SubmitReviewErrors = {};
  // The API takes no review without a class or product of the school's.
  if (!v.targetType) errors.targetType = "Please choose what you’d like to review";
  else if (v.targetType && !choicesFor(page, v.targetType).some((c) => c.value === v.targetId))
    errors.targetId = v.targetType === "course" ? "Please choose the event you attended" : "Please choose the product you purchased";
  if (!v.firstName.trim()) errors.firstName = "First name is a required field";
  if (!v.lastName.trim()) errors.lastName = "Last name is a required field";
  const email = v.email.trim();
  if (!email) errors.email = "Email is required";
  else if (!EMAIL_REGEX.test(email)) errors.email = "Please enter a valid email address";
  if (v.rating < 1) errors.rating = "How many stars would you rate it?";
  const text = v.text.trim();
  if (!text) errors.text = "Please say something about your experience";
  else if (text.length < 5) errors.text = "Your review has to be at least 5 characters long.";
  return errors;
}

/** POST /widget/:slug/submit-review's body (ratings are out of 10 there).
 * Only sent once validateSubmitReview passes, so the target is set. */
export function submitReviewPayload(v: SubmitReviewValues) {
  return {
    firstName: v.firstName.trim(),
    lastName: v.lastName.trim(),
    userEmail: v.email.trim(),
    value: v.rating * 2,
    text: v.text.trim(),
    showOnlyFirstName: v.showOnlyFirstName,
    targetType: v.targetType,
    targetId: v.targetId,
  };
}
